import React, { useCallback, useMemo } from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import isEqual from "lodash/isEqual";
import uniq from "lodash/uniq";

import { EditIcon } from "fe-shared/src/components/Icons";
import { Company, MaterialUnit } from "fe-shared/src/types/domain";
import useMaterialsByCode from "fe-shared/src/hooks/useMaterialsByCode";

import DocumentsInfo from "../DocumentsInfo";
import { isTransactionWithWeightAndScale } from "../../helpers/transactions";
import {
  CorrectionsFormContent,
  Transaction,
  LastModified,
  UpdatedBy,
  CorrectionMaterial,
  CorrectionsData,
  DocumentsResponse,
  CommentResponse,
  Corrections,
  ScaleTicketCorrectionsForm,
  CorrectionMetaData,
  UpdatedChanges,
  TransactionParticipant,
} from "../../types";

import styles from "./CorrectionsDetailsView.module.scss";
import useTenantOptions from "fe-shared/src/hooks/useTenantOptions";

interface Props {
  transaction: Transaction;
  corrections: Corrections | null;
  draftCorrections?: CorrectionsFormContent | null;
  company?: Company;
}

const CorrectionsDetailsView = ({
  transaction,
  corrections,
  draftCorrections,
  company,
}: Props) => {
  const { t } = useTranslation();
  const [ tenantOptions ] = useTenantOptions();
  const materialsByCode = useMaterialsByCode();
  const companyId = company?.companyId;
  const participantRole =
    ((company?.businessRoleCode as unknown) as UpdatedBy) || UpdatedBy.eSRP;
  const companiesById = useMemo(() => {
    const source = transaction.sourceWarehouse?.participant!;
    const target = transaction.targetWarehouse?.participant!;

    return [source, target].filter(Boolean).reduce((result, participant) => {
      result[participant.companyId] = participant;
      return result;
    }, {} as { [key: number]: TransactionParticipant });
  }, [transaction]);

  const isSubmitted = corrections?.isSubmitted;

  const isWithWeightAndScale = useMemo(
    () => isTransactionWithWeightAndScale(transaction),
    [transaction]
  );

  const initialData: CorrectionsData = useMemo(() => {
    if (corrections) {
      return corrections.initialData;
    }

    const {
      materials,
      materialAttributes,
      grossWeight,
      tareWeight,
      scaleTickets,
    } = transaction;

    const ticketNumbers: ScaleTicketCorrectionsForm[] = scaleTickets.map(
      (item) => ({
        ticketId: item.ticketId,
        initialTicketNumber: item.ticketNumber.toString(),
        correctedTicketNumber: item.ticketNumber.toString(),
      })
    );

    const mainCorrection = {
      materials,
      materialAttributes,
      documentsData: [],
      commentsData: [],
    };

    return {
      ...mainCorrection,
      ...(isWithWeightAndScale
        ? {
            grossWeight,
            tareWeight,
            scaleTickets: ticketNumbers,
          }
        : {}),
    };
  }, [isWithWeightAndScale, corrections, transaction]);

  const initialCorrections: CorrectionsData =
    corrections?.correctedData || initialData;

  const currentCorrection: CorrectionsData = useMemo(() => {
    if (!draftCorrections) {
      return initialCorrections;
    }

    const commentsData: CorrectionMetaData<CommentResponse>[] = [
      ...initialCorrections.commentsData,
    ];

    if (draftCorrections.comment) {
      commentsData.push({
        comment: draftCorrections.comment,
        updatedBy: participantRole,
        updatedByCompanyId: companyId,
      });
    }

    const documentsData: CorrectionMetaData<DocumentsResponse>[] = [
      ...initialCorrections.documentsData,
    ];

    if (draftCorrections.attachments.length) {
      const documents = draftCorrections.attachments.map((item) => ({
        documentPath: item.path!,
        name: item.name,
        size: item.size,
        description: item.description,
        updatedBy: participantRole,
        updatedByCompanyId: companyId,
      }));

      documentsData.push(...documents);
    }

    return {
      ...draftCorrections,
      commentsData,
      documentsData,
      ...(isWithWeightAndScale
        ? {
            scaleTickets: draftCorrections.scaleTickets || [],
          }
        : {}),
    };
  }, [
    isWithWeightAndScale,
    draftCorrections,
    initialCorrections,
    participantRole,
    companyId,
  ]);

  const compareMaterials = useCallback(
    (materials1: CorrectionMaterial[], materials2: CorrectionMaterial[]) => {
      return materials1.reduce((result, materialItem1) => {
        const { materialCode } = materialItem1;
        const material2 = materials2.find(
          (materialItem2) => materialItem2.materialCode === materialCode
        );

        if (
          materialItem1.units.unitQuantity !== material2?.units.unitQuantity
        ) {
          const key = `materials.${materialCode}`;

          result[key] = {
            fieldName: key,
            updatedBy: participantRole,
            updatedByCompanyId: companyId,
          };
        }

        return result;
      }, {} as Record<string, CorrectionMetaData<LastModified>>);
    },
    [participantRole, companyId]
  );

  const compareTicketsNumbers = useCallback(
    (
      initialTickets: ScaleTicketCorrectionsForm[] = [],
      tickets: ScaleTicketCorrectionsForm[] = []
    ) =>
      tickets.reduce((result, ticket, indx) => {
        if (
          initialTickets[indx]?.correctedTicketNumber !==
          ticket.correctedTicketNumber
        ) {
          const key = `scaleTickets.${indx}`;

          result[key] = {
            fieldName: key,
            updatedBy: participantRole,
            updatedByCompanyId: companyId,
          };
        }

        return result;
      }, {} as Record<string, CorrectionMetaData<LastModified>>),
    [participantRole, companyId]
  );

  const lastModified: CorrectionMetaData<LastModified>[] = useMemo(() => {
    if (!draftCorrections) {
      return corrections?.lastModified || [];
    }

    const result = (corrections?.lastModified || []).reduce((res, item) => {
      res[item.fieldName] = item;

      return res;
    }, {} as Record<string, CorrectionMetaData<LastModified>>);

    const compareDraftResult = {
      ...compareMaterials(
        initialCorrections.materials,
        draftCorrections.materials
      ),
      ...compareMaterials(
        draftCorrections.materials,
        initialCorrections.materials
      ),
      ...(isWithWeightAndScale
        ? compareTicketsNumbers(
            initialCorrections.scaleTickets,
            draftCorrections.scaleTickets
          )
        : {}),
      ...Object.keys(initialCorrections)
        .filter(
          (key) =>
            ![
              "materials",
              "scaleTickets",
              "commentsData",
              "documentsData",
            ].includes(key)
        )
        .reduce((res, key) => {
          if (!isEqual(initialCorrections[key], draftCorrections[key])) {
            res[key] = {
              fieldName: key,
              updatedBy: participantRole,
              updatedByCompanyId: companyId,
            };
          }
          return res;
        }, {} as Record<string, CorrectionMetaData<LastModified>>),
    };

    return Object.values({ ...result, ...compareDraftResult });
  }, [
    companyId,
    compareMaterials,
    compareTicketsNumbers,
    corrections?.lastModified,
    draftCorrections,
    initialCorrections,
    participantRole,
    isWithWeightAndScale,
  ]);

  const units =
    transaction.materials[0]?.units.unit === MaterialUnit.Kg
      ? t("units.Kg")
      : "";

  const getMaterialsCorrection = (key: string) => {
    const code = key.substr(key.indexOf(".") + 1);

    const initialMaterial = initialData.materials.find(
      (item) => item.materialCode === code
    );
    const currentMaterial = currentCorrection?.materials.find(
      (item) => item.materialCode === code
    );

    return `${materialsByCode[code]?.name || code} ${
      initialMaterial?.units.unitQuantity || 0
    } ${units} → ${currentMaterial?.units.unitQuantity || 0} ${units}`;
  };

  const getMaterialAttributesCorrections = (
    lastModifiedArr: LastModified[]
  ) => {
    const materialsAttributeLM = lastModifiedArr.find(
      (item) => item.fieldName === "materialAttributes"
    );

    return materialsAttributeLM
      ? currentCorrection.materialAttributes
          .map((item) => materialsByCode[item]?.name)
          .join(", ") || t("transactions.details.materialAttributes.removedAll")
      : null;
  };

  const getWeightCorrection = (key: string) =>
    `${t(`transactions.details.weights.${key}Long`)}: ${initialData?.[key]} ${t(
      "units.Kg"
    )} → ${currentCorrection?.[key]} ${t("units.Kg")}`;

  const getScaleTicketsCorrection = (key: string) => {
    const indx = +key.substr(key.indexOf(".") + 1);
    const ticket = currentCorrection.scaleTickets![indx];

    return `${t(`correction.scaleTickets.${indx}`)} ${
      ticket?.initialTicketNumber || ""
    } → ${ticket?.correctedTicketNumber}`;
  };

  const renderComments = (comments: CorrectionMetaData<CommentResponse>[]) => (
    <>
      {comments.map((commentData) => (
        <span key={commentData.correctionCommentId}>{commentData.comment}</span>
      ))}
    </>
  );

  const renderCorrections = (lastModifiedArr: LastModified[]) => {
    const materialsModifications = lastModifiedArr.filter((item) =>
      item.fieldName.includes("materials")
    );

    const weightModification = lastModifiedArr.filter((item) =>
      item.fieldName.includes("Weight")
    );

    const scaleTicketsModifications = lastModifiedArr.filter((item) =>
      item.fieldName.includes("scaleTickets")
    );

    const materialAttributesCorrections = getMaterialAttributesCorrections(
      lastModifiedArr
    );

    return (
      <>
        {materialsModifications.map((item) => (
          <span key={item.fieldName}>
            {getMaterialsCorrection(item.fieldName)}
          </span>
        ))}
        {materialAttributesCorrections && (
          <span>
            {`${t(
              "correction.dialog.form.otherStuff"
            )}: ${materialAttributesCorrections}`}
          </span>
        )}
        {isWithWeightAndScale &&
          weightModification.map((item) => (
            <span key={item.fieldName}>
              {getWeightCorrection(item.fieldName)}
            </span>
          ))}
        {isWithWeightAndScale &&
          scaleTicketsModifications.map((item) => (
            <span>{getScaleTicketsCorrection(item.fieldName)}</span>
          ))}
      </>
    );
  };

  const updateByOrder = useMemo(() => {
    const lastModifiedByCompanyId = draftCorrections
      ? companyId
      : corrections?.lastModifiedByCompanyId;

    const lastModifiedArray = [
      lastModifiedByCompanyId,
      transaction.sourceWarehouse?.participant.companyId,
      transaction.targetWarehouse?.participant.companyId,
    ].filter(Boolean);

    return uniq(lastModifiedArray as number[]);
  }, [
    corrections?.lastModifiedByCompanyId,
    draftCorrections,
    transaction,
    companyId,
  ]);

  const getAllChanges = useCallback(
    (filterFunction: (arg: CorrectionMetaData<unknown>) => boolean) => {
      const modified = lastModified.filter(filterFunction);
      const comments = currentCorrection.commentsData.filter(filterFunction);
      const documents = currentCorrection.documentsData.filter(filterFunction);
      const hasChanges: boolean = [
        modified.length,
        comments.length,
        documents.length,
      ].some(Boolean);

      return {
        modified,
        comments,
        documents,
        hasChanges,
      } as UpdatedChanges;
    },
    [lastModified, currentCorrection]
  );

  const renderChanges = ({ modified, comments, documents }: UpdatedChanges) => (
    <>
      {renderCorrections(modified)}
      {renderComments(comments)}
      {!!documents.length && <DocumentsInfo attachments={documents} />}
    </>
  );

  const updatedByOpCompany = (item: CorrectionMetaData<unknown>) =>
    item.updatedBy === UpdatedBy.eSRP;

  const getCompanyTitle = (currentCompany?: TransactionParticipant) =>
    currentCompany
      ? `${t(`correction.updatedBy.${currentCompany.businessRoleCode}`)} (${
          currentCompany.displayName
        })`
      : "";

  const opCompanyChanges = getAllChanges(updatedByOpCompany);
  const hasAnyChanges = getAllChanges(() => true).hasChanges;
  const requestTitle = corrections?.requestTitle;

  return (
    <div className={styles.transactionDataWrapper}>
      <div
        className={classnames([styles.correctionWrapperEditing], {
          [styles.isNotSubmitted]: !isSubmitted || !!draftCorrections,
        })}
      >
        {(!isSubmitted || !!draftCorrections) && (
          <EditIcon fillColor="#EEC200" className={styles.editIcon} />
        )}
        <div className={styles.correctionWrapper}>
          <h3 className={styles.correctionHeader}>
            {t("transactions.details.corrections.title")}
          </h3>

          {(!!requestTitle || opCompanyChanges.hasChanges) && (
            <div className={styles.correctionLine}>
              <span>{t(`correction.updatedBy.${UpdatedBy.eSRP}`, {tenantName: tenantOptions.tenantName})}</span>
              {requestTitle && <span>{requestTitle}</span>}
              {renderChanges(opCompanyChanges)}
            </div>
          )}
          {!!corrections?.lastModifiedByCompanyId && !hasAnyChanges && (
            <div className={styles.correctionLine}>
              <span>
                {getCompanyTitle(
                  companiesById[corrections.lastModifiedByCompanyId]
                )}
              </span>
              <div>{t("transactions.details.corrections.noChanges")}</div>
            </div>
          )}
          {(updateByOrder as number[]).map((updatedByCompanyId) => {
            const currentCompany = companiesById[updatedByCompanyId];
            const filterByCompanyId = (item: CorrectionMetaData<unknown>) =>
              item.updatedByCompanyId === updatedByCompanyId;

            const changesByCompany = getAllChanges(filterByCompanyId);

            if (currentCompany && changesByCompany.hasChanges) {
              return (
                <div key={updatedByCompanyId} className={styles.correctionLine}>
                  <span>{getCompanyTitle(currentCompany)}</span>
                  {renderChanges(changesByCompany)}
                </div>
              );
            }

            return null;
          })}
        </div>
      </div>
    </div>
  );
};

export default CorrectionsDetailsView;
